import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { LANDING } from '@/constants/testIds';
import { scrollToId } from '@/hooks/useLenis';
import useIsMobile from '@/hooks/useIsMobile';
import Countdown from './Countdown';

const RevealLine = ({ children, delay = 0, className = '' }) => (
    <span className="mask-line block leading-[0.9]">
        <motion.span
            initial={{ y: '110%' }}
            animate={{ y: '0%' }}
            transition={{ duration: 1.0, delay, ease: [0.76, 0, 0.24, 1] }}
            className={className}
        >
            {children}
        </motion.span>
    </span>
);

const Hero = () => {
    const { scrollY } = useScroll();
    const yImg = useTransform(scrollY, [0, 800], [0, 120]);
    const scaleImg = useTransform(scrollY, [0, 800], [1, 1.08]);
    const opacityOverlay = useTransform(scrollY, [0, 500], [1, 0.6]);
    const isMobile = useIsMobile();

    const DESKTOP_IMG = 'https://customer-assets-39nsmqrw.emergentagent.net/job_landing-hub-764/artifacts/flvdi7nn_5b56dbb5-7a75-4d65-a279-fe2a16ce7849.JPG';
    const MOBILE_IMG = 'https://customer-assets-39nsmqrw.emergentagent.net/job_landing-hub-764/artifacts/e9zs9mbn_475256697_18265735585258760_2652486111200570951_n.jpg';

    return (
        <section id="hero" className="relative min-h-[100svh] w-full overflow-hidden">
            {/* Cinematic BG — CSS background-image (responsive) with framer-motion parallax */}
            <motion.div
                style={{
                    y: yImg,
                    scale: scaleImg,
                    backgroundImage: `url(${isMobile ? MOBILE_IMG : DESKTOP_IMG})`,
                    backgroundSize: 'cover',
                    backgroundPosition: isMobile ? 'center 30%' : 'center 45%',
                    backgroundRepeat: 'no-repeat',
                }}
                className="absolute inset-0 cinema-img"
                aria-label="La Tribu Incubatour en FITUR Madrid"
                role="img"
            />

            {/* Vignette + gradient overlay */}
            <motion.div style={{ opacity: opacityOverlay }} className="absolute inset-0 vignette" />
            <div className="grain" aria-hidden />

            {/* Brand glow */}
            <div className="pointer-events-none absolute -bottom-40 -left-20 h-[500px] w-[500px] rounded-full" style={{ background: 'radial-gradient(closest-side, rgba(225,29,72,0.35), transparent 70%)' }} />
            <div className="pointer-events-none absolute -top-32 -right-10 h-[420px] w-[420px] rounded-full" style={{ background: 'radial-gradient(closest-side, rgba(245,158,11,0.18), transparent 70%)' }} />

            {/* Content */}
            <div className="relative z-10 mx-auto flex min-h-[100svh] max-w-[1440px] flex-col justify-between px-6 md:px-10 pt-28 md:pt-32 pb-10">
                {/* Top row */}
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.05 }}
                    className="flex flex-wrap items-center justify-between gap-4"
                >
                    <div className="flex flex-wrap items-center gap-3">
                        <span className="pill" style={{ borderColor: 'rgba(225,29,72,0.5)', background: 'rgba(225,29,72,0.08)', color: '#fff' }}>
                            <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--brand)] animate-pulse" />
                            Primera edición individual · Cupos limitados
                        </span>
                        <span className="pill" style={{ borderColor: 'rgba(245,158,11,0.4)', background: 'rgba(245,158,11,0.06)', color: '#fff' }}>
                            Meses de consultoría · no solo el viaje
                        </span>
                    </div>
                    <div className="hidden md:flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-white/70">
                        <span>Madrid · IFEMA</span>
                        <span className="mx-2 opacity-50">/</span>
                        <span>20 · Ene · 2027</span>
                    </div>
                </motion.div>

                {/* Main title block */}
                <div className="max-w-6xl">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.15 }}
                        className="eyebrow-brand mb-6"
                    >
                        Misión FITUR 2027 · Incubatour
                    </motion.div>

                    <h1
                        data-testid={LANDING.heroTitle}
                        className="font-display text-white text-5xl xs:text-6xl sm:text-7xl md:text-[10vw] lg:text-[9vw] tracking-[-0.005em] leading-[0.9]"
                    >
                        <RevealLine delay={0.2}>Ve a FITUR</RevealLine>
                        <RevealLine delay={0.38}>
                            <span className="italic-accent text-[color:var(--brand)] font-normal">con quien </span>
                            <span>sabe</span>
                            <span className="italic-accent text-[color:var(--brand)] font-normal">.</span>
                        </RevealLine>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.75 }}
                        className="mt-6 md:mt-8 max-w-[54ch] text-base md:text-xl leading-[1.55] text-white/85"
                    >
                        <strong className="text-white">Meses de consultoría</strong> de internacionalización — no solo el viaje.
                        Para operadores, agencias, hoteles, asociaciones, autoridades y cajas de compensación de LATAM
                        que quieren volver de Madrid con
                        <span className="italic-accent text-[color:var(--amber)]"> negocios cerrados</span>, no con folletos.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.9 }}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <button
                            data-testid={LANDING.heroCtaBook}
                            onClick={() => scrollToId('sesion')}
                            className="btn-primary"
                        >
                            Agendar sesión de orientación
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>

                        <a
                            data-testid={LANDING.heroCtaWhatsapp}
                            href="https://wa.me/573158475169?text=Hola%20Incubatour%2C%20quiero%20saber%20si%20aplico%20a%20la%20Misi%C3%B3n%20FITUR%202027"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-ghost"
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.762-1.653-2.06-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.226 1.36.194 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                            </svg>
                            WhatsApp directo
                        </a>

                        <div className="hidden md:flex items-center gap-3 pl-4 border-l border-white/15">
                            <div className="flex -space-x-2">
                                {[0, 1, 2].map(i => (
                                    <span key={i} className="h-8 w-8 rounded-full border-2 border-[color:var(--bg)] bg-gradient-to-br from-white/25 to-white/5" />
                                ))}
                            </div>
                            <div className="text-xs text-white/70">
                                <div className="font-semibold text-white">+25 años</div>
                                <div>guiando destinos LATAM</div>
                            </div>
                        </div>
                    </motion.div>
                </div>

                {/* Bottom row: countdown + micro-stats */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 1.05 }}
                    className="mt-10 md:mt-16 grid grid-cols-12 gap-4 md:gap-6 items-end"
                >
                    <div className="col-span-12 md:col-span-7">
                        <Countdown target="2027-01-20T09:00:00Z" />
                    </div>
                    <div className="col-span-12 md:col-span-5 grid grid-cols-3 gap-3">
                        {[
                            { k: '255K', l: 'visitantes' },
                            { k: '156', l: 'países' },
                            { k: '9.5K+', l: 'empresas' },
                        ].map((s, i) => (
                            <div key={i} className="border-l border-white/20 pl-3">
                                <div className="num-display text-white text-2xl md:text-4xl">{s.k}</div>
                                <div className="text-[9px] md:text-[10px] uppercase tracking-[0.18em] text-white/60 mt-1">{s.l}</div>
                            </div>
                        ))}
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default Hero;
